import { debounce } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import API from 'api/API';
import { NamedSelectOption } from 'components/DropDown/ValidatingSelect';
import { replaceTimeInfoWithUtcInDate } from 'utils/helpers/DateTimeFormat.helpers';

export type SurgeryReferenceCompoundKeyFields = {
  surgeryReference?: string;
  supplier?: NamedSelectOption;
  surgeon?: NamedSelectOption;
  surgeryDate?: Date;
  patientInitials?: string;
};

const useValidateSurgeryReference = <T extends SurgeryReferenceCompoundKeyFields>(fields: T) => {
  const [surgeryReferenceValid, setSurgeryReferenceValid] = useState(true);
  const validateSurgeryReferenceRequestId = useRef(0);

  const validateSurgeryReference = useCallback(
    async (surgeryReference: string) => {
      if (!surgeryReference || !fields.supplier?.id || !fields.surgeon?.id || !fields.surgeryDate) {
        setSurgeryReferenceValid(true);
        return;
      }

      try {
        const { isValid, requestId } = await API.deliveries.validateSurgeryReference({
          supplierTenantId: fields.supplier?.id,
          surgeonId: fields.surgeon?.id,
          surgeryDate: replaceTimeInfoWithUtcInDate(fields.surgeryDate),
          surgeryReference: surgeryReference,
          requestId: ++validateSurgeryReferenceRequestId.current,
          patientInitials: fields.patientInitials,
        });

        if (requestId != validateSurgeryReferenceRequestId.current) return;

        setSurgeryReferenceValid(isValid);
      } catch (err) {
        API.errorHandler(err);
      }
    },
    [fields.patientInitials, fields.supplier?.id, fields.surgeon?.id, fields.surgeryDate],
  );

  const validateSurgeryReferenceDebounced = useMemo(
    () => debounce(validateSurgeryReference, 400),
    [validateSurgeryReference],
  );

  return { surgeryReferenceValid, validateSurgeryReference, validateSurgeryReferenceDebounced };
};

export default useValidateSurgeryReference;
